import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Constante } from '../../constante';
import { ResponseApi } from '../models/response-api';
import { TransaccionApi } from '../models/api/pedido-api';
import { AppUtil } from '../utility/app-util';

export interface ZonaCentroApi {
    lat: number;
    lng: number;
}

export interface ZonaEntregaApi {
    uuid: string;
    nombre: string;
    color: string;
    centro: ZonaCentroApi;
    radioMetros: number;
    establecimientoId: string | null;
    deliveryServicioUuid: string | null;
}

export interface SucursalApi {
    uuid: string;
    nombre: string;
    direccion: string | null;
    latitud: number | null;
    longitud: number | null;
}

export interface CotizarApi {
    precio: number;
    establecimientoId: string | null;
    zonaUuid: string | null;
    zonaNombre: string | null;
}

@Injectable({ providedIn: 'root' })
export class ZonaService {
    private http = inject(HttpClient);
    private url = AppUtil.addTrailingSlash(environment.URL_API_PEDIDOS) + 'api/zona-entrega/';

    listZonas(): Observable<ZonaEntregaApi[]> {
        return this.http.get<ResponseApi<ZonaEntregaApi[]>>(this.url + 'list-zonas').pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK && Array.isArray(res.data) ? res.data : []))
        );
    }

    listSucursales(): Observable<SucursalApi[]> {
        return this.http.get<ResponseApi<SucursalApi[]>>(this.url + 'list-sucursales').pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK && Array.isArray(res.data) ? res.data : []))
        );
    }

    createZona(body: { nombre: string; color: string; centro: ZonaCentroApi; radioMetros: number; establecimientoId?: string | null; deliveryServicioUuid?: string | null }): Observable<TransaccionApi> {
        return this.http.post<ResponseApi<TransaccionApi>>(this.url + 'create-zona', body).pipe(
            map((res) => this.requireData(res))
        );
    }

    updateZona(uuid: string, body: { nombre: string; color: string; centro: ZonaCentroApi; radioMetros: number; establecimientoId?: string | null; deliveryServicioUuid?: string | null }): Observable<TransaccionApi> {
        return this.http.put<ResponseApi<TransaccionApi>>(this.url + 'update-zona/' + uuid, body).pipe(
            map((res) => this.requireData(res))
        );
    }

    deleteZona(uuid: string): Observable<TransaccionApi> {
        return this.http.delete<ResponseApi<TransaccionApi>>(this.url + 'delete-zona/' + uuid).pipe(
            map((res) => this.requireData(res))
        );
    }

    cotizar(lat: number, lng: number): Observable<CotizarApi> {
        return this.http.post<ResponseApi<CotizarApi>>(this.url + 'cotizar', { lat, lng }).pipe(
            map((res) => {
                if (res.success !== Constante.REQUEST_STATUS_OK || !res.data) {
                    const errors = res.errors as { message?: string }[] | null;
                    throw new Error(errors?.[0]?.message || res.message || 'No se pudo cotizar.');
                }
                return res.data;
            })
        );
    }

    private requireData(res: ResponseApi<TransaccionApi>): TransaccionApi {
        if (res.success !== Constante.REQUEST_STATUS_OK || !res.data) {
            const errors = res.errors as { message?: string }[] | null;
            throw new Error(errors?.[0]?.message || res.message || 'Ocurrió un error inesperado.');
        }
        return res.data;
    }
}
